/**
 * BackupRestore — pestaña de Ajustes para respaldo y restauración local.
 *
 * Backend:
 *   - GET  /api/backup/summary  → counts por colección
 *   - GET  /api/backup/export   → descarga .zip
 *   - POST /api/backup/restore  → multipart (file, password, confirm=ELIMINAR)
 */
import { useEffect, useState } from "react";
import { api } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Download, Upload, ShieldAlert, FileArchive, Lock,
  CheckCircle2, AlertTriangle, Cloud, HardDrive, Calendar,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Spanish labels for the count rows.
const COLL_LABEL = {
  customers: "Clientes",
  products: "Productos",
  suppliers: "Proveedores",
  orders: "Pedidos",
  invoices: "Facturas",
  returns: "Devoluciones",
  credit_notes: "Notas de crédito",
  payments: "Pagos",
  stock_movements: "Movimientos de inventario",
  purchase_orders: "Órdenes de compra",
  costings: "Costeos",
  expenses: "Gastos",
  documents: "Documentos (metadatos)",
  tasks: "Tareas",
  companies: "Empresa",
  preferences: "Preferencias",
  route_history: "Rutas (historial)",
  employees: "Colaboradores",
  audit_logs: "Auditoría",
  sellers: "Vendedores (cuentas)",
};

function SummaryRows({ counts }) {
  if (!counts) return null;
  const entries = Object.entries(counts).filter(([, n]) => n > 0);
  if (entries.length === 0) {
    return (
      <p className="text-xs text-slate-500" data-testid="backup-empty-summary">
        Tu cuenta aún no tiene datos para respaldar.
      </p>
    );
  }
  return (
    <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="backup-summary-list">
      {entries.map(([k, n]) => (
        <li key={k} className="flex items-center justify-between border-b border-slate-100 py-1">
          <span className="text-slate-600">{COLL_LABEL[k] || k}</span>
          <span className="font-bold text-slate-800 mono">{n}</span>
        </li>
      ))}
    </ul>
  );
}

export default function BackupRestore() {
  const [summary, setSummary] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Restore state
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [ack, setAck] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);

  const loadSummary = async () => {
    try {
      const { data } = await api.get("/backup/summary");
      setSummary(data);
    } catch {
      // silent — section still works
    }
  };

  useEffect(() => { loadSummary(); }, []);

  const downloadBackup = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/backup/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      // Try to grab filename from Content-Disposition; fall back to a date string.
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      const filename = (m && m[1]) || `minegocio-respaldo-${new Date().toISOString().slice(0, 10)}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Respaldo descargado");
    } catch {
      toast.error("Error al descargar el respaldo");
    } finally {
      setDownloading(false);
    }
  };

  const restoreReady =
    !!file && !!password && confirmText === "ELIMINAR" && ack && !restoring;

  const submitRestore = async () => {
    if (!restoreReady) return;
    setRestoring(true);
    setRestoreResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("password", password);
      form.append("confirm", confirmText);
      const { data } = await api.post("/backup/restore", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRestoreResult(data);
      toast.success("Restauración completada");
      // Reset confirm fields but keep result panel visible.
      setPassword(""); setConfirmText(""); setAck(false); setFile(null);
      loadSummary();
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) toast.error("Contraseña incorrecta");
      else toast.error(err.response?.data?.detail || "Error al restaurar");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="backup-restore-tab">
      {/* Intro / how-to */}
      <Card className="bg-white border border-slate-200 rounded-sm p-6" data-testid="backup-intro">
        <div className="flex items-start gap-3 mb-3">
          <div className="p-2 rounded-sm bg-blue-50 text-[rgb(0,47,167)]">
            <FileArchive className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-base font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Respaldo y restauración de tu negocio
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Bajá una copia completa de tus datos a tu computadora. Si algo
              sale mal —error humano, cambio de máquina, fallo de servicio—
              podés restaurar todo en segundos.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          <InstructionCard
            Icon={Calendar}
            title="1. Hacelo seguido"
            text="Descargá un respaldo al menos una vez por semana y antes de cambios grandes (carga masiva, cambio de personal)."
          />
          <InstructionCard
            Icon={Cloud}
            title="2. Guardalo en 2 lugares"
            text="Tu computadora + un servicio en la nube (Google Drive, Dropbox, OneDrive) o un disco externo. Renombrá el archivo con la fecha."
          />
          <InstructionCard
            Icon={HardDrive}
            title="3. Probá restaurar"
            text="Cada 2–3 meses abrí el .zip o probá restaurar en una cuenta de prueba. Un respaldo que nunca probás no es un respaldo."
          />
        </div>
      </Card>

      {/* Backup card */}
      <Card className="bg-white border border-slate-200 rounded-sm p-6" data-testid="backup-download-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-sm bg-emerald-50 text-emerald-700">
              <Download className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                Descargar respaldo (.zip)
              </h3>
              <p className="text-xs text-slate-500 mt-1 max-w-xl">
                Incluye clientes, productos, pedidos, facturas, inventario,
                cuentas por cobrar, gastos, configuración y más. Los archivos
                binarios de Documentos NO entran al respaldo — solo sus
                metadatos. Descargá los documentos importantes por separado
                si los necesitás offline.
              </p>
            </div>
          </div>
          <Button
            onClick={downloadBackup}
            disabled={downloading}
            className="btn-primary rounded-sm whitespace-nowrap"
            data-testid="backup-download-button"
          >
            <Download className="h-4 w-4 mr-2" />
            {downloading ? "Generando…" : "Descargar respaldo"}
          </Button>
        </div>

        <div className="mt-5 border-t border-slate-100 pt-4">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400 mb-2">
            Contenido del respaldo
          </p>
          <SummaryRows counts={summary?.counts} />
        </div>
      </Card>

      {/* Restore card */}
      <Card className="bg-white border border-amber-200 rounded-sm p-6" data-testid="backup-restore-card">
        <div className="flex items-start gap-3 mb-3">
          <div className="p-2 rounded-sm bg-amber-50 text-amber-700">
            <Upload className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Restaurar desde archivo
            </h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">
              Reemplaza <strong>todos</strong> los datos actuales por los del archivo .zip.
              Esto no se puede deshacer. Solo se permite restaurar un respaldo
              creado por esta misma cuenta.
            </p>
          </div>
        </div>

        <div className="rounded-sm border border-amber-300 bg-amber-50/60 p-3 mb-4 text-xs text-amber-900 flex items-start gap-2" data-testid="backup-restore-warning">
          <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold mb-0.5">Antes de continuar</p>
            <p>
              Recomendamos descargar primero un respaldo del estado actual
              (botón verde arriba) por si necesitás volver atrás. Después
              elegí el archivo, ingresá tu contraseña, escribí ELIMINAR y
              marcá la casilla de confirmación.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium mb-1">Archivo de respaldo (.zip)</label>
            <Input
              type="file"
              accept=".zip,application/zip"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="rounded-sm"
              data-testid="backup-restore-file-input"
            />
            {file && (
              <p className="text-[11px] text-slate-500 mt-1 truncate" data-testid="backup-restore-filename">
                {file.name} · {(file.size / 1024).toFixed(0)} KB
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium mb-1 flex items-center gap-1">
              <Lock className="h-3 w-3" /> Contraseña actual
            </label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Tu contraseña"
              className="rounded-sm"
              autoComplete="current-password"
              data-testid="backup-restore-password"
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">
              Escribí <span className="mono font-bold">ELIMINAR</span> para confirmar
            </label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="ELIMINAR"
              className="rounded-sm mono"
              data-testid="backup-restore-confirm-text"
            />
          </div>

          <div className="flex items-start gap-2 md:pt-6">
            <Checkbox
              checked={ack}
              onCheckedChange={(v) => setAck(!!v)}
              data-testid="backup-restore-ack"
            />
            <label className="text-xs leading-snug cursor-pointer" onClick={() => setAck(!ack)}>
              Entiendo que se <strong>reemplazarán todos</strong> los datos actuales
              y que esta acción <strong>no se puede deshacer</strong>.
            </label>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button
            onClick={submitRestore}
            disabled={!restoreReady}
            className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
            data-testid="backup-restore-submit"
          >
            <Upload className="h-4 w-4 mr-2" />
            {restoring ? "Restaurando…" : "Restaurar ahora"}
          </Button>
        </div>

        {restoreResult && (
          <div className="mt-4 rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-xs" data-testid="backup-restore-result">
            <div className="flex items-center gap-2 font-semibold text-emerald-800 mb-2">
              <CheckCircle2 className="h-4 w-4" />
              Restauración completada
            </div>
            <p className="text-emerald-900">
              Respaldo del {restoreResult.manifest?.generated_at?.slice(0, 19).replace("T", " ")} ·
              Cuenta {restoreResult.manifest?.tenant_email}
            </p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-2 text-emerald-900/90">
              {Object.entries(restoreResult.restored_counts || {})
                .filter(([, n]) => n > 0)
                .map(([k, n]) => (
                  <li key={k} className="flex justify-between">
                    <span>{COLL_LABEL[k] || k}</span>
                    <span className="mono font-bold">{n}</span>
                  </li>
                ))}
            </ul>
            <p className="mt-2 flex items-center gap-1 text-amber-800">
              <AlertTriangle className="h-3 w-3" />
              Refrescá la página (F5) para asegurar que todos los listados muestren los datos restaurados.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}

function InstructionCard({ Icon, title, text }) {
  return (
    <div className="rounded-sm border border-slate-200 bg-slate-50/60 p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 text-[rgb(0,47,167)]" />
        <h4 className="text-sm font-semibold">{title}</h4>
      </div>
      <p className="text-xs text-slate-600 leading-relaxed">{text}</p>
    </div>
  );
}
